import { Suspense, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { skipToken } from '@apollo/client';
import { Query } from 'src/modules/common/components/Query/Query';
import {
    getSensorsAccessoriesPageConfig, SensorsAccessoriesPageData,
    SensorsAccessoriesPageVariables,
} from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { useLocationState } from 'src/sensors/useLocationState.ts';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { SensorAccessoriesPage } from 'src/sensors/accessories/SensorsAccessoriesPage.tsx';

export const PrefetchAccessories = () => {
    const [locationState,, searchParams] = useLocationState();

    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);

    const pageConfig = useMemo(() => getSensorsAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const [
        filter,
    ] = useFilterLogic(pageConfig.filterConfig, searchParams);

    const variables = useMemo((): {
        variables: SensorsAccessoriesPageVariables;
    } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        return {
            variables: {
                filter: composeFilter(
                    mandatoryFilterConfig.config,
                    mandatoryFilterConfig.values,
                    userSettings.currentLocale,
                ),
                label: locationState.sensorLabel,
                locale: userSettings.currentLocale,
            },
        };
    }, [
        filter,
        mandatoryFilterConfig,
        userSettings.currentLocale,
        locationState.sensorLabel,
    ]);

    return (
        <Suspense fallback={<SkeletonMainLayout breadcrumbs={false}/>}>
            <Query<SensorsAccessoriesPageData, SensorsAccessoriesPageVariables>
                query={pageConfig.query}
                variables={variables}
            >
                {({ data }) => {
                    if (data === undefined) {
                        return (<SkeletonMainLayout/>);
                    }
                    return (
                        <SensorAccessoriesPage
                            data={data}
                        />
                    );
                }}
            </Query>
        </Suspense>
    );
};
